import { apiFetch } from './client.js';

/** How a partner holds money for our customers. */
export type AccountModel = 'INTERNAL' | 'DEDICATED_IBAN' | 'SHARED_ACCOUNT';

export interface IntegrationField {
  key: string;
  label: string;
  help: string;
  secret: boolean;
  required: boolean;
  /** `currency-map` holds one value per currency. */
  kind: 'text' | 'url' | 'uuid' | 'number' | 'currency-map' | 'select';
  /**
   * A Treasury setting (DEV-139): stored in the versioned settings store and
   * edited under Settings -> Treasury -> Accounts or -> Commission transfer,
   * never on the vendor card.
   */
  section?: 'TREASURY_ACCOUNTS' | 'TREASURY_COMMISSION';
  /** The values a `select` field may hold. */
  options?: string[];
  placeholder?: string;
  /** Set when this value can be looked up from the partner instead of typed. */
  discover?: 'PARTNER_ACCOUNTS';
  /** For a secret, this is all the console ever learns about its value. */
  isSet: boolean;
  value: string | number | Record<string, unknown> | null;
}

/**
 * One job a vendor's connection can do.
 *
 * A vendor may offer several. They share the credentials and switch on
 * separately, because the same GuruPay instance that issues per-customer
 * vIBANs is the same one that could hold everybody on its master account.
 */
export interface IntegrationMechanic {
  /** Stable code stored on every account opened here, e.g. GURUPAY_VIBAN. */
  id: string;
  variant: string;
  label: string;
  description: string;
  accountModel: AccountModel;
  currencies: string[];
  /** Set when the mechanic is specified but the vendor contract is unconfirmed. */
  contractStatus?: string;
  /** Usable right now: the connection is complete and this switch is on. */
  enabled: boolean;
  /** This mechanic's own switch, whether or not the connection is complete. */
  switchedOn: boolean;
  /** True for our own ledger, which has nothing to switch. */
  alwaysOn: boolean;
  /** What this mechanic still needs — the connection, plus its own fields. */
  missingFields: string[];
  /** Everything it needs is present, whether or not its switch is on. */
  ready: boolean;
  /** Its own settings, on top of the vendor's connection. */
  fields: IntegrationField[];
}

/** A vendor connection: credentials once, mechanics on top. */
export interface Integration {
  vendorId: string;
  vendor: string;
  kind: 'PAYMENT' | 'IDENTITY' | 'MESSAGING';
  /** True for our own ledger — no credentials, nothing to reach. */
  nothingToConfigure: boolean;
  fields: IntegrationField[];
  /** False while a required field is still missing. */
  configured: boolean;
  missingFields: string[];
  mechanics: IntegrationMechanic[];
  updatedBy: string | null;
  updatedAt: string | null;
}

export const integrationsApi = {
  list: (): Promise<Integration[]> => apiFetch('/admin/settings/integrations'),

  /**
   * Save a vendor's connection and which of its mechanics are on. A secret
   * omitted from `secrets` keeps its stored value; an empty string removes it.
   * Secrets are never returned.
   */
  update: (
    vendorId: string,
    input: {
      config?: Record<string, unknown>;
      secrets?: Record<string, string>;
      mechanics?: Record<string, boolean>;
      /** Why. Required (the API refuses without it) for any credential (DEV-133). */
      reason?: string;
    },
  ): Promise<Integration> =>
    apiFetch(`/admin/settings/integrations/${vendorId}`, { method: 'PUT', body: input }),

  /**
   * Ask the partner what accounts exist under the configured customer.
   *
   * A master account UUID names an account underneath a customer and appears
   * on no page of their dashboard, so the console asks their API rather than
   * asking the operator to find it.
   */
  discover: (
    vendorId: string,
  ): Promise<{
    ok: boolean;
    detail: string;
    accounts: Array<{
      uuid: string;
      type: string;
      name: string;
      iban: string;
      bic: string;
      currency: string;
      status: string;
      /** account_type is "virtual_parent" — the one their docs call the master. */
      isMaster: boolean;
    }>;
    /** The master's uuid, when one of them is it. */
    masterUuid?: string;
    keysSeen?: string[];
    rawSample?: string;
  }> => apiFetch(`/admin/settings/integrations/${vendorId}/discover`, { method: 'POST' }),

  /** Calls the partner for real. */
  /** For an email or SMS provider, `to` receives a real test message. */
  test: (vendorId: string, to?: string): Promise<{ ok: boolean; detail: string }> =>
    apiFetch(`/admin/settings/integrations/${vendorId}/test`, { method: 'POST', ...(to === undefined ? {} : { body: { to } }) }),

  /**
   * Tell the partner where to send their webhooks. Without this an
   * asynchronously-issued IBAN never reaches us and the account waits forever.
   */
  registerWebhook: (vendorId: string, url: string): Promise<{ ok: boolean; detail: string }> =>
    apiFetch(`/admin/settings/integrations/${vendorId}/register-webhook`, {
      method: 'POST',
      body: { url },
    }),
};

// The settings History moved to settings-history.api.ts (DEV-137).
export type { SettingsHistoryEntry } from './settings-history.api.js';
